/**
 * Author: Andrey Morozov
 * Confidential. Proprietary and confidential. Unauthorized copying or distribution is prohibited.
 */

import { CostMeter } from "@iam/design-system/agent";
import { useTranslation } from "@iam/design-system/i18n";
import type { SessionMetrics } from "../lib/eventMapping";

export type MetricsStripProps = {
  metrics: SessionMetrics;
  iterations: number;
  /** Per-session cost ceiling, for the CostMeter budget. */
  budgetUsd: number;
  "data-testid"?: string;
};

/**
 * MetricsStrip renders the run's aggregated metrics: a CostMeter (spend vs the
 * session budget) plus token / call counters. It is fed by computeMetrics over
 * the session's audit trail.
 */
export function MetricsStrip({
  metrics,
  iterations,
  budgetUsd,
  "data-testid": testId,
}: MetricsStripProps) {
  const t = useTranslation();
  return (
    <section className="console-metrics" data-testid={testId} aria-label={t("metrics.title")}>
      <CostMeter
        used={metrics.costUsd}
        budget={budgetUsd}
        currency="USD"
        size="sm"
        data-testid="console-cost-meter"
        breakdown={[
          {
            label: t("metrics.modelCalls"),
            amount: metrics.costUsd,
            units: `${metrics.modelCalls}`,
          },
        ]}
      />
      <Metric label={t("metrics.tokens")} value={String(metrics.totalTokens)} />
      <Metric label={t("metrics.toolCalls")} value={String(metrics.toolCalls)} />
      <Metric label={t("metrics.modelCalls")} value={String(metrics.modelCalls)} />
      <Metric label={t("metrics.iterations")} value={String(iterations)} />
    </section>
  );
}

/** Single labeled metric cell used inside MetricsStrip. */
function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="console-metric">
      <span className="console-metric__value">{value}</span>
      <span className="console-metric__label">{label}</span>
    </div>
  );
}
